/**
 * Determines whether a given (job, report) tuple falls into a flow's scope.
 * Pure function — no side effects. Single source of truth for flow scope rules.
 *
 * Used by:
 *   - useReportManagement.populateFlowItems (initial flow population on creation)
 *   - utils/flowAutoAdd.autoAddJobToFlowsStandalone (job create/update trigger)
 *   - utils/flowAutoAdd.autoAddReportToFlowsStandalone (report change trigger)
 *   - backend cron auto-flow-sync (TS port mirrors this logic)
 *
 * @param {object} flow      Flow row with flow_type, start_date, end_date, job_types,
 *                           source_type, report_type_ids, filter_config, is_active, is_archived
 * @param {object} job       Job row (id, job_type, created_at, company_id, ...)
 * @param {object|null} report  Report from job_reports[] (id, reportType, status,
 *                              startDate, endDate, reportTags). Null for JOB_LIST flows.
 * @returns {boolean}
 */
/**
 * Bir isin akisin is turu secimine girip girmedigi.
 *
 * flow.job_types iki nesil deger tasiyabiliyor:
 *   - SAYI  → job_types.id (yeni, dogru olan). Isler ekrani da bununla
 *             filtreliyor, dolayisiyla iki ekran ayni sonucu verir.
 *   - METIN → eski enum yazimi ('AR_GE', 'TEKNOPARK'...). 2026-10 oncesi
 *             kurulmus akislarda boyle duruyor; migration ile id'ye
 *             cevriliyor ama cevrilmemis kayit kalabilir diye destek
 *             surduruluyor.
 *
 * Metin eslesmesi neden tek basina yetmiyor: legacy jobs.job_type kolonunun
 * FK'si migration 1783100000000'de kaldirildi, kolon denetimsiz serbest
 * metne dondu. Prod'da ayni dinamik turu (job_types.id = X, adi
 * 'AR_GE Merkezi') gosteren 64 isin 61'inde legacy metin 'AR_GE', 3'unde
 * 'AR_GE Merkezi' yaziyor. Metne bakan her filtre bu 3 isi kaybediyordu.
 * id'ye bakan filtre 64'u de buluyor.
 */
function matchJobType(jobTypes, job) {
  const ids = new Set();
  const names = new Set();
  for (const entry of jobTypes) {
    if (entry == null) continue;
    if (typeof entry === 'number') { ids.add(entry); continue; }
    const asText = String(entry);
    // "12" gibi sayisal string'ler de id sayilir (jsonb round-trip emniyeti).
    if (/^\d+$/.test(asText)) ids.add(Number(asText));
    else names.add(asText);
  }

  if (ids.size > 0) {
    const jobTypeId = job.job_type_id ?? job.jobTypeId ?? job.job_type_rel?.id
      ?? job.job_type_obj?.id;
    if (jobTypeId != null && ids.has(Number(jobTypeId))) return true;
  }

  if (names.size > 0) {
    // Legacy metin ve dinamik ad — ikisinden biri tutarsa yeter.
    const candidates = [
      typeof job.job_type === 'string' ? job.job_type : null,
      job.job_type_rel?.name,
      job.job_type_obj?.name,
      typeof job.job_type === 'object' ? job.job_type?.name : null,
    ];
    if (candidates.some((c) => c && names.has(c))) return true;
  }

  return false;
}

export function matchFlowScope(flow, job, report = null) {
  if (!flow || flow.is_archived || flow.is_active === false) return false;
  if (!job) return false;

  // Job type filter (always applied, both source types).
  const jobTypes = Array.isArray(flow.job_types) ? flow.job_types : [];
  if (jobTypes.length > 0 && !matchJobType(jobTypes, job)) return false;

  const flowStart = flow.start_date ? new Date(flow.start_date) : null;
  const flowEnd = flow.end_date ? new Date(flow.end_date) : null;

  if (flow.source_type === 'JOB_LIST') {
    // JOB_LIST mode is "give me ALL jobs of these types right now" — the
    // flow's start/end dates describe a reporting window for REPORT_PLAN
    // flows, not a job-creation cutoff. Filtering by job.created_at here
    // would silently drop legacy jobs created before the user opened the
    // flow, which isn't what the user asked for.
    //
    // Optional job_status narrowing: empty array = all statuses (legacy
    // behaviour), otherwise the job's status must be one of the picks.
    const statuses = Array.isArray(flow.job_statuses) ? flow.job_statuses : [];
    if (statuses.length > 0 && !statuses.includes(job.job_status)) return false;
    return true;
  }

  // REPORT_PLAN: report must exist and pass tag/date/type filters
  if (!report) return false;
  if (report.status === 'COMPLETED') return false;

  const reportStart = report.startDate ? new Date(report.startDate) : null;

  // Gelecekteki raporları gizle SADECE sürekli akış (CONTINUOUS) icin —
  // henuz baslamamis rapor "aktif" degildir. Donem bazli (PERIOD_BASED)
  // akista donem tarihleri gelecege kaydiyor olabilir; donem penceresi
  // icinde kalan raporlar (start_date > today olsa bile) planlama icin
  // gorunmelidir. Ornek: Kloia dönem-bazli akis, 1 Tem 2026 - 28 Sub 2027
  // arasi 2 rapor (biri 1 Tem, digeri 1 Eyl basliyor); bugun 8 Tem ise
  // ikincisini de listelemek gerekir.
  if (reportStart && flow.flow_type === 'CONTINUOUS') {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (reportStart > today) return false;
  }

  // Rapor BASLANGIC tarihi flow penceresine dusuyorsa dahil (bitis
  // tarihi ne kadar uzarsa uzasin fark etmez). Kloia rapor 2 = start
  // 01.09.2026, flow 01.01-30.09.2026 → basi flow icinde, dahil.
  if (flowStart && reportStart && reportStart < flowStart) return false;
  if (flowEnd && reportStart && reportStart > flowEnd) return false;

  // Report type filter (only enforced when configured)
  const reportTypeIds = Array.isArray(flow.report_type_ids)
    ? flow.report_type_ids
    : [];
  if (reportTypeIds.length > 0) {
    const rt = String(report.reportType ?? '');
    if (!reportTypeIds.map(String).includes(rt)) return false;
  }

  // Tag filter (only enforced when configured) — overlap semantics
  const tagFilter = Array.isArray(flow.filter_config?.tags)
    ? flow.filter_config.tags
    : [];
  if (tagFilter.length > 0) {
    const reportTags = Array.isArray(report.reportTags) ? report.reportTags : [];
    if (!tagFilter.some((t) => reportTags.includes(t))) return false;
  }

  return true;
}

export default matchFlowScope;
